import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * A floating audio player that appears only when the "national_day" template
 * is active. It plays the Saudi national anthem / patriotic song as ambient
 * background audio. The user must click to start (browser autoplay policy)
 * and can toggle mute/play at any time.
 *
 * The audio file should be placed at /frontend/public/audio/national-anthem.mp3
 * Alternatively set window.__ND_AUDIO_SRC to override the path.
 */

const AUDIO_SRC =
  window.__ND_AUDIO_SRC ||
  process.env.PUBLIC_URL + "/audio/national-anthem.mp3";

const NationalDayAudio = () => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hasAudio, setHasAudio] = useState(true);
  const autoStarted = useRef(false);

  useEffect(() => {
    const check = () => {
      const attr = document.documentElement.getAttribute("data-site-template");
      setVisible(attr === "national_day");
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-site-template"],
    });
    return () => observer.disconnect();
  }, []);

  // Auto-play when template becomes active
  useEffect(() => {
    if (visible && !autoStarted.current && audioRef.current) {
      autoStarted.current = true;
      const audio = audioRef.current;
      audio.volume = 0.3;
      audio.play().then(() => setPlaying(true)).catch(() => {
        // Browser blocked autoplay — start on first user click anywhere
        const startOnClick = () => {
          audio.volume = 0.3;
          audio.play().then(() => setPlaying(true)).catch(() => {});
          document.removeEventListener("click", startOnClick);
        };
        document.addEventListener("click", startOnClick, { once: true });
      });
    }
    if (!visible && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
    }
  }, [visible]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.volume = 0.3;
      audio.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [playing]);

  if (!visible || !hasAudio) return null;

  return (
    <>
      <audio
        ref={audioRef}
        src={AUDIO_SRC}
        loop
        preload="none"
        onError={() => setHasAudio(false)}
      />
      <button
        onClick={toggle}
        aria-label={playing ? t("Mute") : t("Play anthem")}
        title={playing ? t("Mute") : t("Play anthem")}
        className="nd-audio-btn"
      >
        <span className="nd-audio-icon">{playing ? "🔊" : "🔇"}</span>
        <span className="nd-audio-label">
          {playing ? t("Playing") : t("National Anthem")}
        </span>
        {playing && (
          <span className="nd-audio-bars">
            <span /><span /><span /><span />
          </span>
        )}
      </button>
    </>
  );
};

export default NationalDayAudio;
